import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {Plane, Video, FileText, ShieldCheck, MapPin, Globe, ArrowRight, CheckCircle2, Star, Users, Calendar, Compass, Palmtree, Mountain, Ship} from 'lucide-react';
import BookingModal from '../components/BookingModal';

const Home = () => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [searchParams] = useSearchParams();

  // Auto-open booking modal if openBooking param is present
  useEffect(() => {
    if (searchParams.get('openBooking') === 'true') {
      setIsBookingModalOpen(true);
    }
  }, [searchParams]);

  const carouselImages = [
    {
      url: 'https://static.lumi.new/a2/a291f42fd95b2e3635c551660d54f545.png',
      title: 'Virtual Consults from Home',
      subtitle: 'Expert travel health guidance from the comfort of your home'
    },
    {
      url: 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1200',
      title: 'Explore Ancient Wonders',
      subtitle: 'Professional travel health guidance for heritage destinations'
    },
    {
      url: 'https://static.lumi.new/ca/cad1069dc8b1b0ac1dcae142946cee70.png',
      title: 'Expert Care from Dr. Saba Baig',
      subtitle: 'Professional travel health guidance from our founder'
    },
    {
      url: 'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=1200',
      title: 'Adventure Destinations',
      subtitle: 'Expert advice for every journey'
    },
    {
      url: 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1200',
      title: 'Mountain Adventures',
      subtitle: 'Comprehensive health preparation for high-altitude travel'
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);
  const steps = [
  {
    number: "01",
    title: "Book Online",
    desc: "Choose your city and time, then fill out our smart pre-trip health questionnaire.",
    icon: <Calendar className="w-6 h-6" />
  },
  {
    number: "02",
    title: "Meet Virtually",
    desc: "Secure video consultation with a certified travel health clinician from home.",
    icon: <Video className="w-6 h-6" />
  },
  {
    number: "03",
    title: "Pick Up Meds",
    desc: "We fax prescriptions and coordinate vaccines with a pharmacy near you.",
    icon: <MapPin className="w-6 h-6" />
  },
  {
    number: "04",
    title: "Travel Safely",
    desc: "Receive your digital vaccination record and personalized travel advice.",
    icon: <Plane className="w-6 h-6" />
  }];


  const benefits = [
  {
    title: "Virtual Travel Consults",
    icon: <Video className="w-8 h-8 text-cyan-400" />,
    desc: "Skip the waiting room. Expert advice from the comfort of your home.",
    image: "https://static.lumi.new/81/819f0e7574e3116d1c5a2251e4a2ca6f.png"
  },
  {
    title: "Personalized Plans",
    icon: <FileText className="w-8 h-8 text-cyan-400" />,
    desc: "Custom vaccine & medication recommendations for your specific itinerary.",
    image: "https://static.lumi.new/07/0705bb86eaa395165c5b575d56a102a4.png"
  },
  {
    title: "Coordinated Care",
    icon: <MapPin className="w-8 h-8 text-cyan-400" />,
    desc: "Prescriptions & vaccines sent directly to a pharmacy or clinic near you.",
    image: "https://static.lumi.new/2e/2e79c98f9684a018ec4a734693d82689.png"
  },
  {
    title: "Secure Digital Records",
    icon: <ShieldCheck className="w-8 h-8 text-cyan-400" />,
    desc: "Access your vaccination history and trip advice securely online.",
    image: "https://static.lumi.new/8e/8e48373822fe13da5c410e13e970b2ad.png"
  }];


  const tripTypes = [
  { title: "Hajj & Umrah", risks: ["Meningococcal", "Heat Stroke"], image: "https://static.lumi.new/3d/3d5f42d41f003dfb03679e919abf6b72.png", path: "/trip-types/hajj-umrah" },
  { title: "Southeast Asia", risks: ["Typhoid", "Japanese Encephalitis"], image: "https://static.lumi.new/bd/bda6cc96ec6cc3666206ffe0fe98b374.png", path: "/trip-types/southeast-asia" },
  { title: "Latin America", risks: ["Dengue", "Travellers' Diarrhea"], image: "https://static.lumi.new/55/55ab2dd8ce1425b5589e90955fb135a8.jpeg", path: "/trip-types/latin-america" },
  { title: "Africa Safari", risks: ["Yellow Fever", "Malaria"], image: "https://static.lumi.new/7d/7d822b897fa2306e17220115ddedc9e0.png", path: "/trip-types/africa-safari" },
  { title: "Business Travel", risks: ["Routine Boosters", "Stress"], image: "https://static.lumi.new/2b/2b7dee83f6aee0e3d15be18fb9aeec49.png", path: "/trip-types/business-travel" }];


  return (
    <div className="flex flex-col w-full overflow-hidden">
      {/* Carousel Hero Section */}
      <section className="relative min-h-[70vh] sm:min-h-[85vh] md:min-h-[90vh] flex items-center justify-center overflow-hidden bg-navy-900">
        {/* Carousel Background */}
        <div className="absolute inset-0 z-0">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, scale: 1.1 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 1.2, ease: "easeInOut" }}
              className="absolute inset-0">
              <img
                src={carouselImages[currentSlide].url}
                alt={carouselImages[currentSlide].title}
                className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-b from-navy-900/70 via-navy-900/50 to-navy-900/90" />
            </motion.div>
          </AnimatePresence>
          
          {/* Decorative travel elements */}
          <div className="absolute top-20 right-20 opacity-10">
            <Compass className="w-32 h-32 text-white animate-spin" style={{animationDuration: '20s'}} />
          </div>
          <div className="absolute bottom-20 left-20 opacity-10">
            <Palmtree className="w-24 h-24 text-white" />
          </div>
        </div>
        
        {/* Carousel Indicators */}
        <div className="absolute bottom-24 sm:bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3">
          {carouselImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                currentSlide === index ? 'w-12 bg-coral-500' : 'w-2 bg-white/40 hover:bg-white/60'
              }`}
            />
          ))}
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}>

            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 border border-white/20 text-white mb-8 backdrop-blur-md shadow-lg">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-400"></span>
              </span>
              <span className="text-sm font-semibold tracking-wide">Now Accepting Patients Across British Columbia</span>
            </motion.div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-heading font-bold text-white mb-4 sm:mb-6 md:mb-8 leading-tight tracking-tight px-2">
              Your Journey Begins<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-coral-400 via-cyan-400 to-blue-400">With Healthy Travel</span>
            </h1>
            
            <p className="max-w-3xl mx-auto text-sm sm:text-base md:text-lg lg:text-xl text-gray-100 mb-6 sm:mb-8 leading-relaxed font-light px-4">
              Expert travel medicine consultations from the comfort of home. Let us help you explore the world safely with personalized health guidance.
            </p>
            
            <div className="flex items-center justify-center gap-2 sm:gap-4 md:gap-6 mb-6 sm:mb-8 md:mb-12 flex-wrap px-4">
              <div className="flex items-center gap-1.5 sm:gap-2 text-white/90 text-xs sm:text-sm">
                <ShieldCheck className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400 shrink-0" />
                <span className="font-medium">Licensed Providers</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 text-white/90 text-xs sm:text-sm">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400 shrink-0" />
                <span className="font-medium">Serving BC</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 text-white/90 text-xs sm:text-sm">
                <Star className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400 shrink-0" />
                <span className="font-medium">Certified</span>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 px-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsBookingModalOpen(true)}
                className="w-full sm:w-auto sm:min-w-[280px] px-8 sm:px-10 py-4 sm:py-5 bg-gradient-to-r from-coral-500 to-coral-400 hover:from-coral-400 hover:to-coral-500 text-white text-base sm:text-lg font-semibold rounded-full shadow-2xl shadow-coral-500/40 transition-all flex items-center justify-center gap-2 sm:gap-3 group touch-manipulation">
                Book Virtual Consult
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Benefits */}
      <section className="py-12 sm:py-16 pb-6 sm:pb-8 bg-white dark:bg-navy-900 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {benefits.map((benefit, index) =>
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="p-6 rounded-2xl bg-white dark:bg-navy-800/50 border border-gray-200 dark:border-white/5 hover:border-cyan-500/30 transition-all hover:shadow-xl hover:shadow-cyan-500/10 group relative overflow-hidden">

                {/* Background Image */}
                <div
                className="absolute inset-0 bg-cover bg-center opacity-30 group-hover:opacity-40 transition-opacity duration-300"
                style={{ backgroundImage: `url(${benefit.image})` }} />

                <div className="absolute inset-0 bg-gradient-to-br from-white/95 to-white/90 dark:from-navy-800/95 dark:to-navy-900/95"></div>
                
                {/* Content */}
                <div className="relative z-10">
                  <div className="mb-4 p-3 bg-white dark:bg-navy-900 rounded-xl w-fit shadow-sm group-hover:scale-110 transition-transform duration-300">
                    {benefit.icon}
                  </div>
                  <h3 className="text-xl font-bold text-navy-900 dark:text-white mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">{benefit.desc}</p>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="pt-12 sm:pt-16 pb-16 sm:pb-24 bg-gray-50 dark:bg-navy-800 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-500/20 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <span className="inline-block px-4 py-2 bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 rounded-full text-sm font-semibold mb-4">Our Process</span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-navy-900 dark:text-white mb-3 sm:mb-4 px-4">Our Process</h2>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 max-w-2xl mx-auto px-4">Four professional steps to ensure you're fully prepared for your international journey.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 relative">
            {/* Connecting Line (Desktop) */}
            <div className="hidden md:block absolute top-12 left-0 w-full h-0.5 bg-gray-200 dark:bg-white/10 -z-0" />

            {steps.map((step, index) =>
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              className="relative z-10 flex flex-col items-center text-center">

                <div className="w-24 h-24 rounded-full bg-white dark:bg-navy-900 border-4 border-gray-100 dark:border-navy-700 flex items-center justify-center mb-6 shadow-xl relative group">
                  <span className="absolute -top-2 -right-2 w-8 h-8 bg-cyan-500 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-lg">
                    {step.number}
                  </span>
                  <div className="text-cyan-600 dark:text-cyan-400 group-hover:scale-110 transition-transform duration-300">
                    {step.icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-navy-900 dark:text-white mb-3">{step.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed px-4">{step.desc}</p>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* Trip Types Preview */}
      <section className="py-12 sm:py-16 md:py-24 bg-navy-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-5" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex items-end justify-between mb-8 sm:mb-12">
            <div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-white mb-2 sm:mb-4">Popular Destinations</h2>
              <p className="text-sm sm:text-base text-gray-400">Tailored advice for every journey.</p>
            </div>
            <Link to="/trip-types" className="hidden md:flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors font-medium">
              View all trips <ArrowRight size={18} />
            </Link>
          </div>

          <div className="flex overflow-x-auto pb-6 sm:pb-8 gap-4 sm:gap-6 snap-x scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
            {tripTypes.map((trip, index) =>
            <Link to={trip.path} key={index}>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="min-w-[260px] sm:min-w-[280px] md:min-w-[320px] snap-start bg-navy-800/50 border border-white/10 rounded-2xl p-4 sm:p-6 hover:border-cyan-500/50 transition-all group cursor-pointer">

                <div className="h-40 mb-6 rounded-xl bg-navy-900/50 overflow-hidden relative">
                  <img 
                    src={trip.image} 
                    alt={trip.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-navy-900/40 to-transparent" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4 group-hover:text-cyan-400 transition-colors">{trip.title}</h3>
                <div className="flex flex-wrap gap-2 mb-6">
                  {trip.risks.map((risk, rIndex) =>
                <span key={rIndex} className="px-3 py-1 rounded-full bg-white/5 text-xs text-gray-300 border border-white/10">
                      {risk}
                    </span>
                )}
                </div>
                <span className="text-sm font-medium text-cyan-500 flex items-center gap-2 group-hover:translate-x-2 transition-transform">
                  Learn more <ArrowRight size={16} />
                </span>
              </motion.div>
            </Link>
            )}
          </div>
        </div>
      </section>

      {/* Comparison / Why Choose Us */}
      <section className="py-12 sm:py-16 md:py-24 bg-white dark:bg-navy-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-navy-900 to-navy-800 rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 lg:p-16 text-white shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
              <div>
                <span className="inline-block px-4 py-2 bg-white/10 text-cyan-300 rounded-full text-sm font-semibold mb-4">Clinical Excellence</span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold mb-4 sm:mb-6">Why Choose Haven Travel Health?</h2>
                <div className="space-y-4 sm:space-y-6">
                  {[
                  "Certified Travel Medicine Specialists",
                  "Access to full range of travel vaccines",
                  "Transparent pricing, no hidden fees",
                  "Evening & weekend virtual appointments"].
                  map((item, i) =>
                  <div key={i} className="flex items-center gap-4">
                      <div className="w-6 h-6 rounded-full bg-cyan-500 flex items-center justify-center shrink-0">
                        <CheckCircle2 size={14} className="text-white" />
                      </div>
                      <span className="text-sm sm:text-base lg:text-lg text-gray-200">{item}</span>
                    </div>
                  )}
                </div>
                <Link to="/about" className="inline-flex items-center gap-2 mt-8 text-cyan-400 hover:text-cyan-300 font-medium">
                  Meet our clinical team
                  <ArrowRight size={16} />
                </Link>
              </div>
              
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 sm:p-8">
                <div className="flex items-center justify-between mb-8 pb-8 border-b border-white/10">
                  <div>
                    <div className="text-sm text-gray-400 uppercase tracking-wider mb-1">Traditional Clinic</div>
                    <div className="text-2xl font-bold text-gray-500 line-through decoration-red-500/50">2-3 Weeks</div>
                    <div className="text-sm text-gray-500">Wait time</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-cyan-400 uppercase tracking-wider mb-1 font-bold">Haven Travel Health</div>
                    <div className="text-3xl font-bold text-white">24-48 Hrs</div>
                    <div className="text-sm text-gray-300">Average wait time</div>
                  </div>
                </div>
                <p className="text-center text-gray-300 italic">
                  "The most convenient way to prepare for my trip. I got my prescription the same day."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Strip */}
      <section className="py-12 sm:py-16 bg-gradient-to-r from-cyan-500 to-blue-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-white mb-4 sm:mb-6 px-4">Ready to Travel with Confidence?</h2>
          <p className="text-white/90 text-sm sm:text-base md:text-lg mb-6 sm:mb-8 max-w-2xl mx-auto px-4">
            Schedule your professional travel health consultation. Appointments available evenings and weekends.
          </p>
          <button
            onClick={() => setIsBookingModalOpen(true)}
            className="inline-flex items-center justify-center px-8 sm:px-10 py-3.5 sm:py-4 bg-white text-blue-600 text-lg sm:text-xl font-bold rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all w-[90%] sm:w-auto sm:min-w-[280px] max-w-md touch-manipulation">
            Book Virtual Consult
          </button>
        </div>
      </section>

      {/* Booking Modal */}
      <BookingModal 
        isOpen={isBookingModalOpen} 
        onClose={() => setIsBookingModalOpen(false)} 
      />
    </div>);

};

export default Home;